import { Component, OnInit, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { Settings } from 'src/app/app.settings.model';
import { AppointmentModel } from 'src/app/core/models/appointment-model';
import { Room } from 'src/app/core/interfaces/room';
import { ColorLabel } from 'src/app/core/interfaces/color-label';
import { FindTimeResultItem } from 'src/app/core/models/find-time-result-item-model';
import { AppSettings } from 'src/app/app.settings';
import { AuthenticateService } from 'src/app/core/services/authenticate.service';
import { SchedulerService } from '../scheduler.service';
import { FindTimeService } from 'src/app/core/services/find-time.service';
import { CustomerSearchResult } from 'src/app/core/models/customer-search-result';
import { Utils } from 'src/app/core/utils';
import { TreatmentModel } from 'src/app/core/models/treatment-model';
import { EntityTypes, TreatmentStatus, SchedulerRecordType, Gender, ConfirmeAppointmentTypes, ApiResponseCode } from 'src/app/core/enums';
import { ScopeService } from 'src/app/core/services/scope.service';
import { TreatmentEntity } from 'src/app/core/interfaces/treatment-entity';
import { AppData } from 'src/app/app.data';
import { PriceListCode } from 'src/app/core/models/price-list-code';
import { CustomerService } from 'src/app/core/services/customer.service';
import { MatDialog } from '@angular/material/dialog';
import { EnterIdDialogComponent } from '../enter-id-dialog/enter-id-dialog.component';
import { CustomerDetails } from 'src/app/core/models/customer-details';
import { SchedulerModel } from 'src/app/core/models/scheduler.model';
import { ModulesService } from 'src/app/core/services/modules.service';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';


@Component({
  selector: 'app-appointment-create',
  templateUrl: './appointment-create.component.html',
  styleUrls: ['./appointment-create.component.scss']
})
export class AppointmentCreateComponent implements OnInit, AfterViewInit {

  private sub: any;
  error: string;
  settings: Settings;
  form: UntypedFormGroup;
  minPrice = 0;
  maxPrice = 999999;
  appointment: AppointmentModel = new AppointmentModel();
  rooms: Room[] = [];
  roomName: string;
  labels: ColorLabel[] = [];
  canceled: boolean = false;
  findTimeResultItem: FindTimeResultItem;
  patientID: number;
  patientName: string = '';
  hasIdNumber: boolean = false;  
  isIdNumberRequired: boolean = true;
  age: string = '';
  duration: number;
  gender: string = '';
  visitSummary: string[] = [];
  phone: string = '';
  schedulerID: number;
  therapistID: number;
  schedulerName: string;
  branchName: string;
  branchID: number;
  start: moment.Moment;
  end: moment.Moment;
  isFinanceModuleEnabled: boolean = this.authService.modules.Finances;
  customerDetails: CustomerDetails;
  schedulers: SchedulerModel[] = [];
  isRoomRequired: boolean = false;
  isWhatsAppEnabled: boolean = false;

  constructor(
    public appSettings: AppSettings,
    public router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private authService: AuthenticateService,
    private scopeService: ScopeService,
    private schedulerService: SchedulerService,
    private customerService: CustomerService,
    private findTimeService: FindTimeService,
    private moduleService: ModulesService,
    public translate: TranslateService,
    private appData: AppData,
    private dialog: MatDialog) {

    this.settings = appSettings.settings;
    this.findTimeResultItem = appData.selectedFindTimeResultItem;



  }

  ngOnInit() {

    if (this.appData.appointment) {
      this.appointment = this.appData.appointment;
      this.appointment.PrevSchedulerID = this.appointment.SchedulerID;
      this.appointment.PrevRoomID = this.appointment.SchedulerRoomID;

    } else if (this.appData.bookAppointment) {
      const bookAppointment = this.appData.bookAppointment;
      this.appointment.Description = bookAppointment.Description;
      this.appointment.LabelColorID = bookAppointment.LabelColorID;
      this.appointment.LabelColor = bookAppointment.LabelColor;
    }


    // we create appointment always from find-time component so there must be a treatment or pricelist code selected
    // as from 2024-12-02 we can create appointment without treatments if NoMandatoryVisitReason=TRUE
    this.schedulers = this.appData.Schedulers || [];
    const findTimeResultItem = this.findTimeResultItem;
    const treatments = this.appData.treatments || [];
    const priceListCodes = this.appData.priceListCodes || [];
    const scheduler = this.schedulers.find(x => x.ID == findTimeResultItem.SchedulerID);
    const isVisitReasonRequired = !(scheduler?.NoMandatoryVisitReason ?? true);
    this.isRoomRequired = scheduler?.IsRoomRequired ?? false;
    if (isVisitReasonRequired && treatments.length === 0 && priceListCodes.length === 0) {
      this.router.navigate(['/find-time']);
    }

    this.initForm();
    this.setColorLabels();
    this.fillFormByFindTimeItem();
    this.getAvailableRooms();
    this.validateIdNumber();
    this.initNotifications();

  }

  ngAfterViewInit() {
    this.settings.loadingSpinner = false;
  }

  initForm() {

    this.form = this.fb.group({
      hideRequired: false,
      floatLabel: 'auto',
      price: [{ value: 0.00, disabled: true }, Validators.compose([Validators.required, Validators.min(this.minPrice), Validators.max(this.maxPrice)])],
      room: '',
      note: ['', Validators.maxLength(256)],
      selectedColor: null,
      sms: [{ value: false, disabled: false }],
      email: [{ value: false, disabled: false }],
      whatsapp: [{ value: false, disabled: false }],
      confirmed: [{ value: ConfirmeAppointmentTypes.None, disabled: false }],
      arrived: [{ value: false, disabled: true }],
      noShow: [{ value: false, disabled: true }],
      paid: [{ value: false, disabled: this.isFinanceModuleEnabled }]
    });

    this.toggleRoomRequiredValidator();

  }

  async initNotifications() {

    this.isWhatsAppEnabled = await this.loadIsWhatsAppEnabledAsync(this.branchID);

    const data = this.appData.customerDetails;
    if (data) {
      this.f.email.setValue(data.NotificationByEmail);
      this.f.sms.setValue(data.NotificationBySms);
      this.f.whatsapp.setValue(data.NotificationByWhatsApp)
      data.Email ? this.f.email.enable() : this.f.email.disable();
      data.Mobile ? this.f.sms.enable() : this.f.sms.disable();
      data.Mobile && this.isWhatsAppEnabled ? this.f.whatsapp.enable() : this.f.whatsapp.disable();
    }
    else if (this.patientID > 0) {
      this.loadData(this.patientID);
    }
  }

  async loadData(customerID) {
    this.customerService.getDetails(customerID).subscribe(result => {
      if (result.Code === ApiResponseCode.Success) {
        this.customerDetails = new CustomerDetails(result.Data.customer);
        this.f.sms.setValue(result.Data.customer.NotificationBySms);
        this.f.email.setValue(result.Data.customer.NotificationByEmail);
        this.f.whatsapp.setValue(this.isWhatsAppEnabled && result.Data.customer.NotificationByWhatsApp);

      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }



  async loadIsWhatsAppEnabledAsync(branchID: number): Promise<boolean> {

    if (!branchID || branchID <= 0) {
      //this.error = this.translate.instant(`ERROR.CODE.${ApiResponseCode.InvalidBranchID}`);
      //console.log(`loadIsWhatsAppEnabledAsync > invalid branchID`);
      return false;
    }


    try {
      const result = await firstValueFrom(
        this.moduleService.isWhatsAppEnabled(branchID)
      );

      if (result?.Code !== ApiResponseCode.Success) {
        throw new Error(this.translate.instant(`ERROR.CODE.${result?.Code}`));
      }

      return result.Data.IsWhatsAppEnabled;
    } catch (err) {
      this.error = err;
      return false; // fallback value
    }
  }


  validateIdNumber() {
    this.hasIdNumber = false;
    this.customerService.hasIDNumber(this.patientID).subscribe({
      next: (result) => {
        if (result?.Code !== ApiResponseCode.Success) {
           throw new Error(this.translate.instant(`ERROR.CODE.${result?.Code}`));
        }
        this.hasIdNumber = result?.Data === ApiResponseCode.Success;
        
      },
      error: (err) => (this.error = err),
    });

    this.schedulerService.shouldAskForIdNumber(this.patientID).subscribe({
      next: (result) => {
        if (result?.Code !== ApiResponseCode.Success) {
           throw new Error(this.translate.instant(`ERROR.CODE.${result?.Code}`));
        }        
        this.isIdNumberRequired = result?.Data === true;
        if (this.isIdNumberRequired) {
          this.showEnterIdNumberDialog(this.patientID);
        }
      },
      error: (err) => (this.error = err),
    });

    

  }

  showEnterIdNumberDialog(customerID: number) {
    const dialogRef = this.dialog.open(EnterIdDialogComponent, {
      width: '300px',
      direction: (this.settings.rtl ? 'rtl' : null),
      data: {
        customerID: customerID,
        idType: 1,
        idNumber: ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'confirmed') {
        // if here id number was saved
        this.hasIdNumber = true;

      } else {
        // enter id number was canceled
        this.onCancel(null);
      }
    });
  }


  setColorLabels() {
    if (this.appData.ColorLabels && this.appData.ColorLabels.length > 0) {
      this.labels = this.appData.ColorLabels;

    } else {
      this.loadColors();
    }
  }

  loadColors() {
    this.schedulerService.getColors().subscribe(result => {
      if (result.Code === 1) {
        // success
        this.labels = this.appData.ColorLabels;

      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    })
  }

  getAvailableRooms() {
    const branchID = this.branchID;
    const date = this.start.toDate();
    const startTime = this.start.format('HH:mm'); // this.start.format('LT');
    const endTime = this.end.format('HH:mm'); // this.end.format('LT');

    const roomId = this.f.room.value;
    this.schedulerService.getAvailableRooms(branchID, date, startTime, endTime, roomId)
      .subscribe(result => {
        if (result.Code === 1) {
          // success
          this.rooms = result.Data as Room[];

        } else {
          this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
        }
      }, error => {
        this.error = error;
      });
  }

  fillFormByFindTimeItem() {
    if (!this.findTimeResultItem) {
      return;
    }

    var appointmentData = null;

    if (this.appData.customerDetails) { // if made appointment through patient appointment history OR 'move appointment'
      var patDetails = this.appData.customerDetails;

      appointmentData = { patient: [], reason: [] };
      // attach history appt customer details.
      appointmentData.patient = {
        EntityID: patDetails.ID,
        Name: patDetails.Name,
        Age: patDetails.Age,
        Phone: patDetails.Mobile,
        Gender: this.translate.instant(`GENDER.${patDetails.Gender}`)
      };


      // attach history appt treatments
      if (this.appData.visitItem) {
        appointmentData.reason = this.appData.visitItem.Treatments;
      } else if (this.appData.bookVisitItem) {
        appointmentData.reason = this.appData.bookVisitItem.Treatments;
      }

    } else if (this.appData.formStateFindTime) { // if made appointment through find-time
      appointmentData = this.appData.formStateFindTime;
    }

    if (!appointmentData) {
      return;
    }

    // fill patient details by the find-time-form
    const patient = appointmentData.patient as CustomerSearchResult;
    this.patientID = +patient.EntityID; // the (+) sign convert string to number
    this.patientName = patient.Name;
    this.age = patient.Age;
    this.phone = patient.Phone;
    this.gender = patient.Gender;

    // this.visitReason = findTimeFormValue.reason;
    const treatments = this.appData.treatments.length > 0 ? this.appData.treatments : this.appData.visitItem ? this.appData.visitItem.Treatments : [];
    const pricelistCodes = this.appData.priceListCodes || [];

    this.visitSummary = this.appData.visitSummary(treatments, pricelistCodes) || [];
    const finalPrice = this.calcFinalPrice(appointmentData, treatments, pricelistCodes);
    this.f.price.setValue(Utils.toDecimal(finalPrice));


    const priceListCode = pricelistCodes.filter(x => x.LabelColorID > 0)[0];
    if (priceListCode != null) {
      const selectedColor = this.labels?.filter(x => x.ID == priceListCode.LabelColorID)[0];
      if (selectedColor != null)
        this.f.selectedColor.setValue(selectedColor);
    }

    const treatmentFromCard = treatments.filter(x => x.PriceListCode?.LabelColorID > 0)[0];
    if (treatmentFromCard != null) {
      const selectedColor = this.labels?.filter(x => x.ID == treatmentFromCard.PriceListCode.LabelColorID)[0];
      if (selectedColor != null)
        this.f.selectedColor.setValue(selectedColor);
    }

    if (this.appointment) {
      this.f.note.setValue(this.appointment.Description ? this.appointment.Description : '');
      if (this.appointment.LabelColorID != null)
        this.f.selectedColor.setValue(this.labels.filter(x => x.ID === this.appointment.LabelColorID)[0]);
      this.f.sms.setValue(this.appointment.NotifyBySms);
      this.f.email.setValue(this.appointment.NotifyByEmail);
      this.f.paid.setValue(this.appointment.Paid);
      this.f.confirmed.setValue(this.appointment.Confirmed);
    }



    // fill appointment details
    const item = this.findTimeResultItem;
    this.duration = item.Duration;
    this.schedulerID = item.SchedulerID;

    this.schedulerName = item.SchedulerName;
    this.branchName = item.BranchName;
    this.branchID = item.BranchID ? item.BranchID : 0;

    // set date and time
    const startArr = item.StartTime.split(':'); // position[0]-hours, position[1]-minutes
    const endArr = item.EndTime.split(':'); // position[0]-hours, position[1]-minutes

    var date = new Date(moment(item.Date).format());  // 2019-07-13T21:00:00.000Z => Sun Jul 14 2019 00:00:00 GMT+0300 (Israel Daylight Time)
    var year = date.getFullYear();
    var month = Utils.pad(date.getMonth() + 1, 2);
    var day = Utils.pad(date.getDate(), 2);

    var startHours = Utils.pad(+startArr[0], 2);
    var startMinutes = Utils.pad(+startArr[1], 2);
    this.start = moment(new Date(`${year}-${month}-${day}T${startHours}:${startMinutes}`));

    var endHours = Utils.pad(+endArr[0], 2);
    var endMinutes = Utils.pad(+endArr[1], 2);
    this.end = moment(new Date(`${year}-${month}-${day}T${endHours}:${endMinutes}`));


    console.log('item.Date: ' + item.Date + ' start: ' + this.start.format() + ' hours: ' + startArr[0]);

    if (this.appData.formStateFindTime) {
      if (item.SchedulerResourceID > 0) {
        this.f.room.setValue(item.SchedulerResourceID);
        this.roomName = this.appData.formStateFindTime.roomName || null;
      }
    }

    const daysPassed = moment().diff(this.start, 'days');

    if (daysPassed < 0) {
      // if here the event is in the future
      this.f.arrived.disable();
      this.f.noShow.disable();
    }
  }




  calcFinalPrice(appointmentData: any, treatments: TreatmentEntity[], pricelistCodes: PriceListCode[]) {
    // let price = 0;

    if (treatments && treatments.length > 0) {
      return treatments.map(item => item.Price).reduce((a, b) => a + b);
    }

    if (pricelistCodes && pricelistCodes.length > 0) {
      return pricelistCodes.map(item => item.FinalPrice).reduce((a, b) => a + b);
    }

    if (appointmentData && appointmentData.reason && appointmentData.reason.length > 0) {
      appointmentData.reason.map(item => {
        if (item.FinalPrice) {
          return item.FinalPrice; // pricelistCode
        }
        return item.Price || 0; // treatment
      }).reduce((a: number, b: number) => a + b);
    }

    return 0;
  }

  getTherapistID(schedulerID: number): number {
    const userScope = this.scopeService.userScope;
    if (userScope) {
      const schedulersInScope = userScope.schedulers;
      if (schedulersInScope) {
        const scheduler = schedulersInScope.find(x => x.ID === schedulerID);
        if (scheduler && scheduler.RecordType === SchedulerRecordType.User) {
          return scheduler.RecordID;
        }
      }
    }
    return 0;
  }


  getTime(date: Date) {
    return Utils.getTime(date);
  }

  toDecimal(value) {
    // this function will be called on blur to fix number 4 => 4.00
    const val = Utils.toDecimal(value);
    this.f.price.setValue(val);
  }

  // convenience getter for easy access to form fields
  get f() { return this.form.controls; }

  // for debug only
  get diagnostic() { return JSON.stringify(this.form.value); }


  onSubmit(formValue) {

    if (!this.canceled) {
      this.appointment.Title = this.patientName;
      this.appointment.Start = this.start;
      this.appointment.End = this.end;
      this.appointment.Duration = this.duration;
      this.appointment.EndTime = this.end.format('HH:mm'); // chaged from 'LT' to 'HH:mm' to solve issue when saving appointemt form english site.
      this.appointment.Hours = this.start.format('HH');
      this.appointment.Minutes = this.start.format('mm');

      this.appointment.EntityID = this.patientID;
      this.appointment.EntityType = EntityTypes.Customer;
      // this.appointment.PriceListRefID = this.visitReason ? this.visitReason.PriceListRefID : 0;

      this.appointment.Price = formValue.price;
      this.appointment.Description = formValue.note;
      this.appointment.NotifyBySms = formValue.sms;
      this.appointment.NotifyByEmail = formValue.email;
      this.appointment.NotifyByWhatsApp = formValue.whatsapp;
      this.appointment.Confirmed = formValue.confirmed;
      this.appointment.Arrived = formValue.arrived;
      this.appointment.NoShow = formValue.noShow;
      this.appointment.Paid = formValue.paid;

      this.appointment.BranchID = this.branchID;
      this.appointment.SchedulerID = this.schedulerID;
      this.appointment.SchedulerRoomID = this.f.room.value;

      // TODO: this can be removed later 
      this.appointment.PriceListRefID = 0;



      // if opened from find-time and treatment selected, visitItem.Treatments incase of making appointment from history
      const treatments = this.appData.treatments.length > 0 ? this.appData.treatments : this.appData.visitItem ? this.appData.visitItem.Treatments : [];

      if (treatments && treatments.length > 0) {
        this.appointment.TreatmentType = treatments[0].ParentTypeID;
        const therapistID = this.schedulerID; // this.getTherapistID(this.schedulerID);

        const treatmentModelCollection = treatments.map(entity => {
          const model = new TreatmentModel(entity);
          model.Selected = true;

          model.TreatmentCodeID = entity.CodeID;
          model.TreatmentCode = entity.PriceListCodeName || '';
          model.TreatmentCodeDescription = entity.Description || '';
          model.PriceList = { Text: '', Value: entity.PriceListID.toString() };
          model.Therapist = { Text: '', Value: therapistID.toString() };

          model.Duration = this.duration;
          model.TreatmentStatusID = TreatmentStatus.ToDo;
          return model;
        });
        // send as string
        this.appointment.TreatmentsFromCard = JSON.stringify(treatmentModelCollection);
      }

      // if open from find-time and pricelistCode seleted
      const pricelistCode = this.appData.priceListCodes;
      if (pricelistCode && pricelistCode.length > 0) {
        // TreatmentsFromPriceList => List of TreatmentModel must set pricelistRefID and Selected
        const treatmentModelCollection = pricelistCode.map(x => {
          this.appointment.TreatmentType = x.TypeID;
          const model = new TreatmentModel();
          model.PriceListRefID = x.PriceListRefID;
          model.Selected = true;

          model.Duration = x.Duration;
          model.PriceList = { Text: '', Value: x.PriceListID.toString() };
          model.Price = x.FinalPrice || 0;
          model.TreatmentCodeID = x.ID;
          model.TreatmentCode = x.Code;
          model.TreatmentCodeName = x.Name;
          model.TreatmentCodeDescription = x.Description;

          const therapistID = this.schedulerID; // this.getTherapistID(this.schedulerID);
          model.Therapist = { Text: '', Value: therapistID.toString() };
          return model;
        });
        this.appointment.TreatmentsFromPriceList = JSON.stringify(treatmentModelCollection);

        // this can be deleted after removing from the model on the server
        this.appointment.PriceListRefID = 0;
      }

      const waitlistItem = this.appData.waitlistItem;
      if (waitlistItem) {
        this.appointment.SchedulerWaitListID = waitlistItem.SchedulerWaitListID;
      }

      if (formValue.selectedColor) {
        this.appointment.LabelColorID = (formValue.selectedColor as ColorLabel).ID;
      } else {
        this.appointment.LabelColorID = null;
      }

      this.create(this.appointment);
    }
  }

  create(appointment: AppointmentModel) {

    this.settings.loadingSpinner = true;

    // change to utc test to fix bug on amazon:
    // appointment.Start = appointment.Start.utc();
    // appointment.End = appointment.End.utc();

    // option 2:
    // appointment.Start = appointment.Start.format('YYYY-MM-DDTHH:mm:ss');
    // appointment.End = appointment.End.format('YYYY-MM-DDTHH:mm:ss')

    this.schedulerService.postEvent(appointment).subscribe(result => {
      if (result.Code === 1) {
        // success
        const eventID = result.Data.EventID;
        this.appData.waitlistItem = null;
        this.router.navigate(['/appointment-saved', eventID]);     

      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
        this.settings.loadingSpinner = false;
      }

    }, error => {
      this.error = error;
      this.settings.loadingSpinner = false;
    });
  }

  onCancel($event) {
    this.canceled = true;
    this.router.navigate(['/find-time']);
  }

  public get confirmeAppointmentType(): typeof ConfirmeAppointmentTypes {
    return ConfirmeAppointmentTypes;
  }

  onClick_markApptAs($event: any) {
    let targetMarkName = $event.currentTarget.getAttribute('for');

    switch (targetMarkName) {
      case 'confirmed':
        {
          if (this.f.confirmed.value !== ConfirmeAppointmentTypes.None) {
            this.f.confirmed.setValue(ConfirmeAppointmentTypes.None);

          } else {
            this.f.confirmed.setValue(ConfirmeAppointmentTypes.Confirmed);
          }
        }
        break;
      default:
        break;
    }
  }

  toggleRoomRequiredValidator() {
    const control = this.form.get('room');
    //const roomID = this.f.room.value;
    if (control) {
      if (this.isRoomRequired) {
        //control.addValidators(Validators.required);
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      } else {
        const currentValidators = control.validator ? [control.validator] : [];
        const updatedValidators = currentValidators.filter(validatorFn => validatorFn !== Validators.required);
        control.setValidators(null);
        control.updateValueAndValidity();
      }
    }
  }
}
